import { parseNumber } from './format.js';
import { sanitizePositiveNumber } from './validation.js';

/** Normalizes any incoming value type to a plain number for arithmetic. Null and empty/whitespace strings resolve to 0. Returns NaN for unparseable strings or NaN inputs. */
function resolveInputValue(value: string | number | null): number {
  if (value === null) {
    return 0;
  }

  if (typeof value === 'string') {
    if (value.trim() === '') {
      return 0;
    }
    const parsed = parseNumber(value);
    return parsed ?? 0;
  }

  return value; // number, possibly NaN
}

/** Returns true when `step` is a finite positive number that can be used for alignment. */
function isValidStep(step: number | undefined): boolean {
  return step !== undefined && !Number.isNaN(step) && step > 0;
}

/**
 * When `parsedValue` falls between two step marks, returns the value snapped to the nearest mark in the given direction.
 * Returns `undefined` when the value already sits on a step boundary (no snap needed).
 */
function computeStepAlignedValue(
  parsedValue: number,
  direction: 1 | -1,
  cleanedStep: number,
  baseValue: number,
): number | undefined {
  const remainder = Math.abs((parsedValue - baseValue) % cleanedStep);
  const softEpsilon = Number.EPSILON * 100;

  if (remainder <= softEpsilon || remainder >= cleanedStep - softEpsilon) {
    return undefined; // already on a boundary
  }

  const nextValue = parsedValue + direction * cleanedStep;

  if (parsedValue < baseValue) {
    return nextValue + remainder - (direction > 0 ? cleanedStep : 0);
  }

  return nextValue - remainder + (direction < 0 ? cleanedStep : 0);
}

/** Clamps `value` to the lower bound if `min` is defined. */
function applyMinConstraint(value: number, min: number | undefined): number {
  if (min === undefined || Number.isNaN(min)) {
    return value;
  }
  return Math.max(value, min);
}

/** Clamps `value` to the upper bound. When `validStep` is set and the clamped value equals `max`, it is further snapped down to the last full step below `max`. */
function applyMaxConstraint(
  value: number,
  max: number | undefined,
  baseValue: number,
  cleanedStep: number,
  validStep: boolean,
): number {
  if (max === undefined || Number.isNaN(max)) {
    return value;
  }

  if (validStep && value >= max) {
    return (
      baseValue + cleanedStep * Math.floor((max - baseValue) / cleanedStep)
    );
  }

  return Math.min(value, max);
}

/** Rounds away floating-point noise beyond 10 decimal places. */
function roundToPrecision(value: number): number {
  const precision = Math.pow(10, 10);
  return Math.round(value * precision) / precision;
}

/**
 * Apply step increment or decrement to a given value, ensuring it adheres to the specified step, min, and max constraints.
 * @internal
 */
export function applyStep(
  value: string | number | null,
  step: number | undefined,
  stepMultiplier: number | undefined,
  min: number | undefined,
  max: number | undefined,
  direction: 1 | -1,
): number | null | string {
  const parsedValue = resolveInputValue(value);

  if (Number.isNaN(parsedValue)) {
    return value;
  }

  const validStep = isValidStep(step);
  const cleanedStep = sanitizePositiveNumber(step);
  const effectiveStepSize =
    Math.trunc(sanitizePositiveNumber(stepMultiplier)) * cleanedStep;
  const baseValue = min || 0;

  let nextValue = parsedValue + direction * effectiveStepSize;

  if (validStep) {
    const aligned = computeStepAlignedValue(
      parsedValue,
      direction,
      cleanedStep,
      baseValue,
    );
    if (aligned !== undefined) {
      nextValue = aligned;
    }
  }

  nextValue = applyMinConstraint(nextValue, min);
  nextValue = applyMaxConstraint(
    nextValue,
    max,
    baseValue,
    cleanedStep,
    validStep,
  );

  return roundToPrecision(nextValue);
}
